import { ReactNode } from "react";
import { Icon } from "~/components/icon/icon";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipArrow,
} from "~/components/ui/tooltip";
import { cn } from "~/utils";
import { Text } from "~/components/text";

type VisibilityMode = "always" | "onParentHover";

interface InfoTooltipProps {
  text?: string | ReactNode;
  /** Names the trigger for screen readers and makes it keyboard-focusable. */
  label?: string;
  testId?: string;
  side?: "top" | "bottom" | "left" | "right";
  children?: ReactNode;
  triggerClassName?: string;
  contentClassName?: string;
  iconSize?: "small" | "medium";
  visibilityMode?: VisibilityMode;
}

export const InfoTooltip = ({
  text,
  label,
  testId,
  triggerClassName = "",
  side = "bottom",
  children,
  contentClassName = "",
  iconSize = "medium",
  visibilityMode = "always",
}: InfoTooltipProps) => {
  const triggerClassNames = cn(
    "text-secondary-400 hover:text-secondary-500 shrink-0",
    visibilityMode === "onParentHover" &&
      "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
    triggerClassName,
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {label ? (
          <button
            type="button"
            aria-label={label}
            className={cn("inline-flex", triggerClassNames)}
            data-testid={testId}
          >
            <Icon name="info" size={iconSize} />
          </button>
        ) : (
          <span className={triggerClassNames} data-testid={testId}>
            <Icon name="info" size={iconSize} />
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        className={cn(
          "text-left shadow-tooltip text-secondary-600 whitespace-normal z-10 border-none max-w-xs",
          contentClassName,
        )}
        sideOffset={6}
      >
        <TooltipArrow
          className="fill-white drop-shadow-xs"
          width={12}
          height={6}
        />
        {children ?? <Text variant="body">{text}</Text>}
      </TooltipContent>
    </Tooltip>
  );
};
